/**
 * Artifact Requirements Panel Component
 * Shows required upload(s) for the scenario—e.g. one PDF (Product Brief) for Discovery & Definition.
 */

import React, { useState } from 'react';
import './ArtifactRequirementsPanel.css';
import { ChevronDown, ChevronUp, CheckCircle, AlertCircle, Upload, FileText } from 'lucide-react';

const ArtifactRequirementsPanel = ({ tasks, artifacts, scenarioId, onOpenWorkspace, inOverlay = false }) => {
  const [expanded, setExpanded] = useState(!inOverlay); // Auto-expand in overlay

  // Check if artifact is uploaded for a task
  const isArtifactUploaded = (taskIndex, requiredArtifact) => {
    if (!requiredArtifact) return null; // No requirement
    
    const taskArtifacts = artifacts.filter(a => 
      a.scenarioId === scenarioId && 
      (a.taskIndex === taskIndex || a.taskIndex === null)
    );

    if (taskArtifacts.length === 0) return false;

    // Check if any artifact matches the required type
    // Match by filename containing required type keywords
    const requiredKeywords = requiredArtifact.toLowerCase().split(' ');
    return taskArtifacts.some(artifact => {
      const fileName = artifact.fileName.toLowerCase();
      // Check if filename contains any of the required keywords
      return requiredKeywords.some(keyword => 
        fileName.includes(keyword) && keyword.length > 2 // Only match meaningful keywords
      ) || fileName.split('.')[0].includes(requiredArtifact.toLowerCase().split(' ')[0]);
    });
  };

  const tasksWithRequirements = tasks.map((task, index) => ({
    ...task,
    index,
    isUploaded: isArtifactUploaded(index, task.requiredArtifact)
  }));

  // Scenario 1 (Discovery & Definition) requires exactly one upload: Product Brief (PDF)
  const isDiscoveryScenario = scenarioId === 'saas-1';
  const allRequired = tasksWithRequirements.filter(t => t.requiredArtifact);
  const requiredTasks = isDiscoveryScenario && allRequired.length > 1
    ? allRequired.slice(-1) // only the single deliverable
    : allRequired;
  const uploadedCount = requiredTasks.filter(t => t.isUploaded === true).length;
  const totalRequired = requiredTasks.length;
  const uploadLabel = totalRequired === 1 ? 'Required Upload' : 'Required Uploads';

  return (
    <div className={`artifact-requirements-panel ${inOverlay ? 'in-overlay' : ''}`}>
      {!inOverlay && (
        <button 
          className="artifact-requirements-toggle"
          onClick={() => setExpanded(!expanded)}
          aria-label={expanded ? `Hide ${uploadLabel.toLowerCase()}` : `Show ${uploadLabel.toLowerCase()}`}
        >
          <FileText size={16} />
          <span>{uploadLabel}</span>
          {totalRequired > 0 && (
            <span className="upload-status-badge">
              {uploadedCount}/{totalRequired}
            </span>
          )}
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      )}

      {(inOverlay || expanded) && (
        <div className="artifact-requirements-content">
          {requiredTasks.map((task) => {
            const isUploaded = task.isUploaded;
            
            return (
              <div key={task.index} className="artifact-requirement-item">
                <div className="requirement-header">
                  <div className="requirement-task">
                    <span className="task-number-small">Task {task.index + 1}</span>
                    <span className="task-title-small">{task.title}</span>
                  </div>
                  <div className="requirement-status">
                    {isUploaded ? (
                      <span className="status-uploaded">
                        <CheckCircle size={16} />
                        Uploaded
                      </span>
                    ) : (
                      <span className="status-missing">
                        <AlertCircle size={16} />
                        Missing
                      </span>
                    )}
                  </div>
                </div>
                <div className="requirement-details">
                  <p className="required-artifact">
                    <strong>Required:</strong> {task.requiredArtifact}
                    {task.artifactDescription && (
                      <span className="artifact-description"> — {task.artifactDescription}</span>
                    )}
                  </p>
                  {!isUploaded && (
                    <button 
                      className="upload-link-button"
                      onClick={() => onOpenWorkspace && onOpenWorkspace()}
                    >
                      <Upload size={14} />
                      Upload to Workspace
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          
          {requiredTasks.length === 0 && (
            <div className="no-requirements">
              <p>No specific upload required for this scenario.</p>
              <p className="hint">Upload any documents you create while working through the tasks.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ArtifactRequirementsPanel;

